import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import './style.css';
import LiveStreamViewer from '../LiveStreamViewer';

export default function ActiveLiveStreams() {
  const { user } = useSelector(state => state);
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStream, setSelectedStream] = useState(null);
  const [canceledStreams, setCanceledStreams] = useState(new Set());
  const normalizeId = (value) => (value !== undefined && value !== null ? String(value) : '');
  
  // Load active streams and canceled streams from localStorage
  useEffect(() => {
    const loadActiveStreams = async () => {
      try {
        const response = await axios.get('/livestream/active', {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        
        if (response.data.success) {
          // Load canceled streams from localStorage
          const savedCanceledStreams = localStorage.getItem('canceledLiveStreams');
          const canceledSet = savedCanceledStreams
            ? new Set(JSON.parse(savedCanceledStreams).map(id => normalizeId(id)))
            : new Set();
          setCanceledStreams(canceledSet);
          
          // Filter out canceled streams
          const filteredStreams = response.data.streams
            .map(stream => ({ ...stream, _id: normalizeId(stream._id) }))
            .filter(stream => !canceledSet.has(stream._id));
          setStreams(filteredStreams);
        }
      } catch (error) {
        console.error('Error loading active streams:', error);
      }
      setLoading(false);
    };
    
    if (user?.token) {
      loadActiveStreams();

      // Fallback sync so ended streams do not remain visible if a socket event is missed.
      const intervalId = setInterval(loadActiveStreams, 15000);
      return () => clearInterval(intervalId);
    }
  }, [user?.token]);
  
  // Listen for real-time live stream events
  useEffect(() => {
    if (!user?.token) return;

    // Use existing socket connection if available
    const socket = window.notificationSocket;
    
    if (socket) {
      // Listen for new live streams
      socket.on('liveStreamStarted', (data) => {
        console.log('New live stream started:', data);
        setStreams(prevStreams => {
          const streamId = normalizeId(data.streamId);

          // Check if stream already exists or is canceled
          const exists = prevStreams.find(s => normalizeId(s._id) === streamId);
          const isCanceled = canceledStreams.has(streamId);
          
          if (!exists && !isCanceled) {
            return [{
              _id: streamId,
              title: data.title,
              description: data.description,
              startedAt: data.startedAt,
              user: data.streamer,
              viewerCount: 0,
              isActive: true
            }, ...prevStreams];
          }
          return prevStreams;
        });
      });

      // Listen for stream ended events
      socket.on('liveStreamEnded', (data) => {
        console.log('Live stream ended:', data);
        const endedStreamId = normalizeId(data.streamId);
        setStreams(prevStreams => 
          prevStreams.filter(s => normalizeId(s._id) !== endedStreamId)
        );
      });

      // Listen for viewer count updates
      socket.on('viewerJoined', (data) => {
        console.log('Viewer joined stream:', data);
        const streamId = normalizeId(data.streamId);
        setStreams(prevStreams => 
          prevStreams.map(s => 
            normalizeId(s._id) === streamId 
              ? { ...s, viewerCount: data.viewerCount }
              : s
          )
        );
      });

      socket.on('viewerLeft', (data) => {
        console.log('Viewer left stream:', data);
        const streamId = normalizeId(data.streamId);
        setStreams(prevStreams => 
          prevStreams.map(s => 
            normalizeId(s._id) === streamId 
              ? { ...s, viewerCount: Math.max(0, (s.viewerCount || 0) - 1) }
              : s
          )
        );
      });

      // Cleanup event listeners
      return () => {
        socket.off('liveStreamStarted');
        socket.off('liveStreamEnded');
        socket.off('viewerJoined');
        socket.off('viewerLeft');
      };
    }
  }, [user?.token, canceledStreams]);
  
  const calculateDuration = (startedAt) => {
    const now = new Date();
    const startTime = new Date(startedAt);
    const diff = Math.floor((now - startTime) / 1000);
    const minutes = Math.floor(diff / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m`;
  };

  // Cancel/remove a stream from the list
  const cancelStream = (streamId, event) => {
    event.stopPropagation(); // Prevent triggering the card click
    
    // Add to canceled streams set
    const newCanceledStreams = new Set(canceledStreams);
    newCanceledStreams.add(streamId);
    setCanceledStreams(newCanceledStreams);
    
    // Save to localStorage
    localStorage.setItem('canceledLiveStreams', JSON.stringify([...newCanceledStreams]));
    
    // Remove from current streams
    setStreams(prevStreams => prevStreams.filter(s => s._id !== streamId));
  };
  
  if (loading) {
    return (
      <div className="active_streams_container">
        <div className="streams_header">
          <h3>🔴 Live Now</h3>
        </div>
        <div className="streams_loading">
          <div className="loading_placeholder"></div>
        </div>
      </div>
    );
  }
  
  if (streams.length === 0) {
    return null; // Don't show anything if no active streams
  }
  
  return (
    <>
      <div className="active_streams_container">
        <div className="streams_header">
          <h3>🔴 Live Now</h3>
          <span className="streams_count">{streams.length} active</span>
        </div>
        
        <div className="streams_list">
          {streams.map((stream) => (
            <div 
              key={stream._id} 
              className="stream_card"
              onClick={() => setSelectedStream(stream._id)}
            >
              <div className="stream_thumbnail">
                <img 
                  src={stream.user?.picture || '/images/default_pic.png'} 
                  alt={`${stream.user?.first_name} ${stream.user?.last_name}`}
                  className="streamer_picture"
                />
                <div className="live_badge">LIVE</div>
                <div className="stream_info_overlay">
                  <div className="viewer_count_badge">
                    👥 {stream.viewerCount || 0}
                  </div>
                  <div className="duration_badge">
                    {calculateDuration(stream.startedAt)}
                  </div>
                </div>
                {/* Cancel button */}
                <button 
                  className="cancel_stream_btn"
                  onClick={(e) => cancelStream(stream._id, e)}
                  title="Remove from list"
                  style={{
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    background: 'rgba(231, 76, 60, 0.9)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10
                  }}
                >
                  ✕
                </button>
              </div>
              
              <div className="stream_details">
                <div className="stream_title" title={stream.title}>
                  {stream.title}
                </div>
                <div className="streamer_name">
                  {stream.user?.first_name} {stream.user?.last_name}
                </div>
                {stream.description && (
                  <div className="stream_description" title={stream.description}>
                    {stream.description}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {selectedStream && (
        <LiveStreamViewer
          streamId={selectedStream}
          onClose={() => setSelectedStream(null)}
        />
      )}
    </>
  );
} 